import {
  Bell, MessageCircle, Users, CalendarDays, UserPlus, UserCheck,
  BookOpen, CircleDot, CheckCircle, Sparkles, Heart, Zap, XCircle,
  KeyRound, Flag,
} from 'lucide-react';

/**
 * Icon je Benachrichtigungs-Typ, Reiter-Filter und Zeitformat: eine Wahrheit
 * für NotificationsPage (Vollbild), NotificationBell (Dropdown) und das
 * WS-Inkrement im NotificationContext.
 *
 * Seit Oktober 2026 (iOS 1.0.3) ohne Ton je Typ: Die Liste zeigt ein
 * neutrales Icon ohne Kreis und ohne Fläche (`text-faint`). Vorher bekam
 * jeder Typ eine eigene Tönung, bis zu fünf Farben in einer Liste. Farbe
 * trägt Bedeutung, nicht Kategorie (CD §2 Regel 6).
 */

const NOTIFICATION_ICONS = {
  CHAT_MESSAGE: MessageCircle,
  CHAT_REQUEST: UserPlus,
  CONNECTION_ACCEPTED: UserCheck,
  CONNECTION_DECLINED: Users,
  NEW_PUBLIC_SESSION: BookOpen,
  SESSION_START: CircleDot,
  SESSION_COMPLETED: CheckCircle,
  SESSION_INVITED: CalendarDays,
  SESSION_REMINDER: CalendarDays,
  SESSION_READMIT_REQUEST: UserPlus,
  WELCOME: Heart,
  AURA_UPDATE: Sparkles,
  BADGE_EARNED: Sparkles,
  PASSWORD_RESET: KeyRound,
  OTTERCONOMY_WEEK_RESOLVED: BookOpen,
  CONTENT_REPORT: Flag,
  CHAT_REPORT: Flag,
  SKILL_REQUEST: Zap,
  SKILL_REQUEST_APPROVED: CheckCircle,
  SKILL_REQUEST_REJECTED: XCircle,
};

/**
 * Liefert { Icon } für einen Benachrichtigungs-Typ.
 * Unbekannte Typen fallen auf die Glocke zurück.
 */
export const getNotificationAppearance = (type) => ({
  Icon: NOTIFICATION_ICONS[type] || Bell,
});

/**
 * Typen je Reiter, nur für den Listenfilter von Seite und Glocke. Die
 * Ungelesen-Zähler (Backend /unread-counts, WS-Inkrement im
 * NotificationContext) bleiben bewusst bei ihren alten Typen: Sie treiben den
 * Punkt am Sessions-Reiter der Navigation (Forschungskopplung, FF 3). Der
 * Reiter „Alle“ zeigt alles außer Chat-Nachrichten.
 */
export const NOTIFICATION_TAB_TYPES = {
  connections: ['CHAT_REQUEST', 'CONNECTION_ACCEPTED', 'CONNECTION_DECLINED'],
  sessions: [
    'SESSION_INVITED',
    'SESSION_REMINDER',
    'SESSION_START',
    'SESSION_COMPLETED',
    'NEW_PUBLIC_SESSION',
    'SESSION_READMIT_REQUEST',
  ],
};

/** Reiter der Liste (ohne Icons: das Wort genügt) und ihr Leerzustand. */
export const NOTIFICATION_TABS = [
  { key: 'all', label: 'Alle', emptyText: 'Du bist auf dem neuesten Stand.' },
  { key: 'connections', label: 'Vernetzungen', emptyText: 'Hier erscheinen Anfragen und Antworten zu Vernetzungen.' },
  { key: 'sessions', label: 'Sessions', emptyText: 'Hier erscheinen Einladungen und Erinnerungen zu Sessions.' },
];

/** Filtert die Liste für einen Reiter. Chat-Nachrichten stehen nie in der Liste. */
export const filterNotificationsByTab = (notifications, tabKey) => {
  const nonChat = (notifications || []).filter((n) => n.type !== 'CHAT_MESSAGE');
  const types = NOTIFICATION_TAB_TYPES[tabKey];
  return types ? nonChat.filter((n) => types.includes(n.type)) : nonChat;
};

/** Zeitstempel einer Benachrichtigung: relativ bis 24 Stunden, danach TT.MM. */
export const formatNotificationTime = (timestamp) => {
  const date = new Date(timestamp);
  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);

  if (diffMins < 1) return 'Gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min`;
  if (diffMins < 1440) return `vor ${Math.floor(diffMins / 60)} Std`;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

export default getNotificationAppearance;
